import React, { useCallback, useMemo, useState } from "react";
import { Button, Editor, Flex, Text } from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import { SPACING } from "#utils/spacing";
import { CommentAuthor } from "../../types";
import * as styles from "./styles_vanilla.css";

type ComposeViewProps = {
  isInternal: boolean;
  onBack?: () => void;
  onSubmit: (body: string) => void | Promise<void>;
  currentUser?: CommentAuthor;
  isGPX?: boolean;
  onCancel?: () => void;
};

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

export const ComposeView: React.FC<ComposeViewProps> = ({
  isInternal,
  onBack,
  onSubmit,
  currentUser,
  isGPX = false,
  onCancel,
}) => {
  const [body, setBody] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const getPlaceholder = () => {
    if (isGPX) return "Add your comment here…";
    if (isInternal) return "Add internal note for other Canopy users…";
    return "Add a reply visible to the client…";
  };
  const placeholder = getPlaceholder();

  const editorInit = useMemo(
    () => ({
      height: 37,
      menubar: false,
      plugins: "lists link autolink",
      toolbar: "bold italic underline | bullist numlist",
      branding: false,
      statusbar: false,
      placeholder,
    }),
    [placeholder],
  );

  const handlePost = useCallback(async () => {
    if (!body.trim() || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onSubmit(body);
    } finally {
      setIsSubmitting(false);
    }
  }, [body, isSubmitting, onSubmit]);

  const visibilityMessage = isInternal
    ? "Your comment will only be visible to other Canopy users"
    : "Your comment will be visible to the client";

  return (
    <div className={styles.composeContainer} data-testid="workflow-comments-compose-view">
      <div className={styles.composeBody}>
        {!isGPX && (
          <div className={styles.composeBackRow}>
            <Button
              variant="neutral"
              displayAsLink
              onClick={onBack}
              icon="arrow-left"
              iconPosition="left"
              data-testid="workflow-comments-compose-back-button"
            >
              Back
            </Button>
          </div>
        )}

        {currentUser && (
          <Flex
            alignItems="center"
            gap={SPACING.SMALL}
            className={styles.composeUserRow}
            data-testid="workflow-comments-compose-user"
          >
            <div className={styles.avatarFallback} aria-hidden="true">
              {getInitials(currentUser.name)}
            </div>
            <Text variant="strong-sm" color={Colors.Neutral900}>
              {currentUser.name}
            </Text>
          </Flex>
        )}

        {!isGPX && (
          <div
            className={styles.composeBanner}
            data-internal={String(isInternal)}
            data-testid="workflow-comments-compose-banner"
          >
            <Text variant="strong-sm" color={Colors.Neutral900}>
              {visibilityMessage}
            </Text>
          </div>
        )}

        <div className={styles.editorWrapper}>
          <Editor
            init={editorInit}
            value={body}
            onEditorChange={(content: string) => setBody(content)}
            data-testid="workflow-comments-compose-editor"
          />
        </div>

        {isGPX && (
          <Text
            variant="secondary-sm"
            color={Colors.Neutral600}
            data-testid="workflow-comments-compose-gpx-notice"
          >
            The fund admin manager will automatically be notified
          </Text>
        )}
      </div>

      <Flex className={styles.composeFooter} gap={SPACING.SMALL}>
        {isGPX && (
          <Button
            variant="neutral"
            onClick={() => {
              setBody("");
              onCancel?.();
            }}
            data-testid="workflow-comments-compose-cancel-button"
          >
            Cancel
          </Button>
        )}
        <Button
          variant="primary"
          onClick={() => {
            void handlePost();
          }}
          disabled={!body.trim() || isSubmitting}
          data-testid="workflow-comments-compose-post-button"
        >
          Comment
        </Button>
      </Flex>
    </div>
  );
};
